// Шапка карточки: название, состояние, ключевые показатели, действия.
//
// ЧТО ЗДЕСЬ ОТВЕЧАЕТ НА КАКОЙ ВОПРОС. Человек, открывший карточку, спрашивает четыре вещи
// подряд: что это, в каком оно состоянии, что по деньгам и срокам, и что я могу с этим
// сделать. Шапка отвечает ровно на них и в этом порядке.
//
// ПОЧЕМУ СОСТОЯНИЕ — ЗНАЧОК, А НЕ ПОЛЕ. Черновик, выпущен, аннулирован — это не то, что
// набирают: это то, что происходит с документом по кнопке. Номер выдаётся при выпуске,
// аннулирование номер не освобождает. Поле ввода тут означало бы, что состояние можно
// поставить любое, минуя проверки.

import { fmtDate, pluralN } from '../lib/format'

import { ЗНАЧОК_СОСТОЯНИЯ } from './types'
import { текстСостояния } from './values'
import type { Действие, ОписаниеКарточки, Состояние, Значения } from './types'
import type { Форма } from './useCardForm'

/** Куда можно перевести документ из текущего состояния. */
export function переходы(документ: boolean, с: Состояние): Состояние[] {
  if (!документ) return с === 'archived' ? ['active'] : ['archived']
  if (с === 'draft') return ['issued']
  if (с === 'issued') return ['cancelled']
  return []
}

const ПОДПИСЬ_ПЕРЕХОДА: Record<string, string> = {
  issued: 'Выпустить',
  cancelled: 'Аннулировать',
  archived: 'В архив',
  active: 'Вернуть из архива',
  draft: 'Вернуть в черновик',
}

interface Props {
  описание: ОписаниеКарточки
  форма: Форма
  /** Карточка открыта только на чтение: права роли или чужая зона. */
  безПравки?: boolean
  onДействие?: (д: Действие) => void
}

export function CardHead({ описание, форма, безПравки, onДействие }: Props) {
  const { сПометкой, состояние, грязная, изменённые, незаполненные, сохранение } = форма
  const название = описание.заголовок(сПометкой)

  const дальше = безПравки ? [] : переходы(описание.документ, состояние)

  return (
    <>
      {/* Заголовок карточки — h2, а не h1, и это не мелочь: h1 на странице один и
          принадлежит РАЗДЕЛУ («Карточки»), иначе пункт меню и заголовок экрана расходятся,
          а проверка tests/zagolovki.test.ts сверяет их дословно. `.page-head h2` в
          styles.css предусмотрен ровно для этого случая. */}
      <div className="page-head">
        <h2>{название}</h2>
        <span className={ЗНАЧОК_СОСТОЯНИЯ[состояние]}>{текстСостояния(состояние)}</span>
        <span className="spacer" />
        {!безПравки && (
          <>
            <button
              type="button"
              className="primary"
              disabled={!грязная || сохранение.идёт}
              onClick={() => { void форма.сохранить() }}
            >
              {сохранение.идёт ? 'Сохраняем…' : 'Сохранить'}
            </button>
            <button type="button" disabled={!грязная || сохранение.идёт} onClick={форма.отменитьПравки}>
              Отменить правки
            </button>
            {дальше.map((с) => (
              <button
                key={с}
                type="button"
                disabled={сохранение.идёт}
                onClick={() => { void форма.сохранить(с) }}
              >
                {ПОДПИСЬ_ПЕРЕХОДА[с] ?? с}
              </button>
            ))}
          </>
        )}
        {(описание.действия ?? []).map((д) => {
          const нельзя = д.почемуНельзя ? д.почемуНельзя(сПометкой) : null
          return (
            <button
              key={д.код}
              type="button"
              className={д.главное ? 'primary' : undefined}
              disabled={Boolean(нельзя)}
              title={нельзя ?? undefined}
              onClick={() => onДействие?.(д)}
            >
              {д.подпись}
            </button>
          )
        })}
      </div>

      {безПравки && (
        <div className="notice">
          Карточка открыта только на чтение: права вашей роли не дают её править. Это
          ограничение стоит на сервере, а не здесь — кнопки спрятаны, чтобы не обещать
          того, чего не будет.
        </div>
      )}

      {форма.черновикОт && (
        <div className="notice">
          Восстановлен черновик от {fmtDate(форма.черновикОт, true)}: вы заполняли эту
          карточку и не сохранили. Набранное подставлено в поля, в базе его ещё нет.{' '}
          <button type="button" onClick={форма.сброситьЧерновик}>Забыть черновик</button>
        </div>
      )}

      {сохранение.ошибка && <div className="notice err">{сохранение.ошибка}</div>}

      {грязная && (
        <div className="notice">
          Не сохранено: {pluralN(изменённые.size, 'поле', 'поля', 'полей')}. Правки лежат
          в черновике этого браузера — в базе их нет, и коллеги их не видят.
        </div>
      )}

      {незаполненные.length > 0 && (
        <div className="notice">
          Осталось заполнить {pluralN(незаполненные.length, 'поле', 'поля', 'полей')}:{' '}
          {незаполненные.map((п) => п.подпись).join(', ')}.
          {описание.документ && состояние === 'draft'
            ? ' Без них карточку можно хранить черновиком, но нельзя выпустить.'
            : ''}
        </div>
      )}

      <Показатели описание={описание} значения={сПометкой} />
    </>
  )
}

function Показатели({ описание, значения }: { описание: ОписаниеКарточки; значения: Значения }) {
  if (!описание.показатели.length) return null
  return (
    <div className="tiles">
      {описание.показатели.map((п) => (
        <div className={`tile${п.тревога?.(значения) ? ' hot' : ''}`} key={п.код}>
          <div className="t-label">{п.подпись}</div>
          <div className="t-sum">{п.значение(значения)}</div>
        </div>
      ))}
    </div>
  )
}
